import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AccessTime,
  BugReport,
  CheckCircle,
  PlayArrow,
  Schedule,
  Numbers,
  Lightbulb,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import NavBar from "../components/NavBar";
import { supabase } from "../supabaseClient";
import axios from "axios";
import { API_URL } from "../globals";
import { format } from "date-fns";
import Chart from "react-apexcharts";

const Overview = () => {
  const [user, setUser] = useState(null);
  const [studios, setStudios] = useState([]);
  const [selectedStudio, setSelectedStudio] = useState("");

  const setSelectedStudioWithLogging = (studioId) => {
    console.log('Overview - setSelectedStudio called with:', studioId);
    setSelectedStudio(studioId);
  };
  const [analytics, setAnalytics] = useState(null);
  const [groups, setGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [metricType, setMetricType] = useState("count");
  const [selectedTimeframe, setSelectedTimeframe] = useState("all");
  const navigate = useNavigate();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 5) return {
      text: "Good night",
      emoji: "🌙"
    };
    if (hour < 12) return {
      text: "Good morning", 
      emoji: "☀️"
    };
    if (hour < 17) return {
      text: "Good afternoon",
      emoji: "🌤️"
    };
    if (hour < 20) return {
      text: "Good evening",
      emoji: "🌇"
    };
    return {
      text: "Good night",
      emoji: "🌙"
    };
  };

  async function getUser() {
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/user`, {
        headers: headers,
      });

      if (response.data) {
        setUser(response.data);
      }
    } catch (error) {
      console.log(error);
    }
  }

  async function getAvailableStudios() {
    console.log('Overview - getAvailableStudios called');
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/studio/seeds`, {
        headers: headers,
      });

      if (response.data) {
        const readyStudios = response.data.filter(studio => studio.status === "READY");
        console.log('Overview - Setting studios to:', readyStudios.map(s => ({id: s.id, name: s.display_name})));
        setStudios(readyStudios);
      }
    } catch (error) {
      console.log('Overview - Error in getAvailableStudios:', error);
    }
  }

  async function fetchAnalytics(studioId) {
    if (!studioId) return;
    
    setIsLoadingAnalytics(true);
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/test/analytics?studio_id=${studioId}`, {
        headers: headers,
      });

      if (response.data) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error("Error fetching analytics:", error);
    } finally {
      setIsLoadingAnalytics(false);
    }
  }

  async function fetchGroups(studioId) {
    if (!studioId) return;
    
    setIsLoadingGroups(true);
    const authToken = (await supabase.auth.getSession()).data.session.access_token;
    const headers = {
      Authorization: `Bearer ${authToken}`,
    };

    try {
      const response = await axios.get(`${API_URL}/test/groups?studio_id=${studioId}`, {
        headers: headers,
      });

      if (response.data) {
        setGroups(response.data);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setIsLoadingGroups(false);
    }
  }

  useEffect(() => {
    getUser();
    getAvailableStudios();
  }, []);

  useEffect(() => {
    console.log('Overview - Studios useEffect triggered, studios.length:', studios.length);
    if (studios.length > 0) {
      // Try to get saved studio from localStorage
      const savedStudio = localStorage.getItem('selectedStudio');
      const savedStudioId = savedStudio ? parseInt(savedStudio, 10) : null;
      console.log('Overview - Retrieved from localStorage:', savedStudio, 'parsed as:', savedStudioId);
      console.log('Overview - Available studios:', studios.map(s => s.id));
      console.log('Overview - Current selectedStudio state:', selectedStudio);
      const savedStudioExists = savedStudioId && studios.find(studio => studio.id === savedStudioId);
      console.log('Overview - Saved studio exists:', !!savedStudioExists, savedStudioExists);
      
      if (savedStudioExists) {
        console.log('Overview - Using saved studio:', savedStudioId);
        setSelectedStudioWithLogging(savedStudioId);
      } else {
        console.log('Overview - Using first studio:', studios[0].id);
        setSelectedStudioWithLogging(studios[0].id);
      }
    }
  }, [studios]);

  useEffect(() => {
    console.log('Overview - selectedStudio useEffect triggered, selectedStudio:', selectedStudio);
    if (selectedStudio) {
      fetchAnalytics(selectedStudio);
      fetchGroups(selectedStudio);
    }
    setIsLoading(false);
  }, [selectedStudio]);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy h:mm a");
    } catch {
      return dateString;
    }
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds || milliseconds === 0) return "0s";
    
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);
    
    if (years > 0) {
      const remainingMonths = Math.floor((days % 365) / 30);
      return remainingMonths > 0 ? `${years}yr ${remainingMonths}mo` : `${years}yr`;
    }
    
    if (months > 0) {
      const remainingDays = days % 30;
      return remainingDays > 0 ? `${months}mo ${remainingDays}d` : `${months}mo`;
    }
    
    if (days > 0) {
      const remainingHours = hours % 24;
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
    }
    
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
    }
    
    if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
    }
    
    return `${seconds}s`;
  };

  const filterOldRunningTests = (groups) => {
    const sixHoursAgo = new Date();
    sixHoursAgo.setHours(sixHoursAgo.getHours() - 6);
    
    return groups.filter(group => {
      // If test has 0 tests (running) and is older than 6 hours, filter it out
      if (group.total_playwright_tests === 0) {
        const createdAt = new Date(group.created_at);
        return createdAt > sixHoursAgo;
      }
      // Keep all completed tests
      return true;
    });
  };

  const formatInsights = (insights, groupId) => {
    if (!insights || !Array.isArray(insights) || insights.length === 0) {
      return null;
    }
    
    return (
      <Box>
        {insights.map((insight, index) => {
          const severity = insight.severity?.toLowerCase() || "";
          const severityEmoji = severity === 'failure' ? '🚨' : severity === 'warning' ? '⚠️' : '⏭️';
          const encodedSummary = encodeURIComponent(insight.summary);
          const url = `/test/group/${groupId}?category=${encodedSummary}`;
          
          return (
            <Box key={index} sx={{ mb: index < insights.length - 1 ? 1 : 0 }}>
              <Typography
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: '#fff',
                  textDecoration: 'none',
                  display: 'block',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#4a90e2'
                  }
                }}
              >
                {severityEmoji} {insight.summary} ({insight.count} tests)
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const calculatePassPercentage = (statusBreakdown) => {
    if (!statusBreakdown) return 0;
    
    const total = statusBreakdown.passed + statusBreakdown.failed + statusBreakdown.skipped;
    if (total === 0) return 0;
    
    return Number(((statusBreakdown.passed / total) * 100).toFixed(1));
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      passed: { color: "#4CAF50", icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      failed: { color: "#F44336", icon: <BugReport sx={{ fontSize: 16 }} /> },
      pending: { color: "#FFA726", icon: <AccessTime sx={{ fontSize: 16 }} /> },
      skipped: { color: "#757575", icon: <PlayArrow sx={{ fontSize: 16 }} /> },
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={status}
        size="small"
        icon={config.icon}
        sx={{
          backgroundColor: `${config.color}20`,
          color: config.color,
          fontWeight: 500,
          textTransform: "capitalize",
        }}
      />
    );
  };

  const StatCard = ({ title, value, subtitle, trend, icon, color }) => (
    <Card
      sx={{
        backgroundColor: "#1a1a1a",
        border: "1px solid #2a2a2a",
        height: "100%",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              backgroundColor: `${color}20`,
              borderRadius: 1,
              p: { xs: 0.75, sm: 1 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {icon}
          </Box>
          {trend !== undefined && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {trend > 0 ? (
                <TrendingUp sx={{ color: "#4CAF50", fontSize: { xs: 18, sm: 20 } }} />
              ) : (
                <TrendingDown sx={{ color: "#F44336", fontSize: { xs: 18, sm: 20 } }} />
              )}
              <Typography
                sx={{
                  color: trend > 0 ? "#4CAF50" : "#F44336",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                  fontWeight: 500,
                }}
              >
                {Math.abs(trend)}%
              </Typography>
            </Box>
          )}
        </Box>
        <Typography sx={{ color: "#888", fontSize: { xs: "0.75rem", sm: "0.875rem" }, mb: 1 }}>
          {title}
        </Typography>
                  <Typography
            sx={{
              color: "#fff",
              fontSize: { xs: "1.25rem", sm: "1.5rem", md: "2rem" },
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
          {value}
        </Typography>
        {subtitle && (
          <Typography sx={{ color: "#666", fontSize: { xs: "0.625rem", sm: "0.75rem" }, mt: 1 }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  const getMetricData = () => {
    if (!analytics) return [];
    
    const timeframes = ["last_24h", "last_7d", "last_30d"];
    const displayNames = {
      last_24h: "Last 24 Hours",
      last_7d: "Last 7 Days", 
      last_30d: "Last 30 Days"
    };
    
    if (metricType === "count") {
      return timeframes.map(tf => ({
        timeframe: displayNames[tf],
        value: analytics.test_counts?.[tf] || 0,
        failed: analytics.failed_test_counts?.[tf] || 0,
        passRate: analytics.test_counts?.[tf] > 0 
          ? ((analytics.test_counts[tf] - analytics.failed_test_counts[tf]) / analytics.test_counts[tf] * 100).toFixed(1)
          : 0
      }));
    } else {
      return timeframes.map(tf => ({
        timeframe: displayNames[tf],
        value: analytics.total_durations_formatted?.[tf] || "0s",
        rawMs: analytics.total_durations_ms?.[tf] || 0,
        avgDuration: analytics.test_counts?.[tf] > 0 
          ? ((analytics.total_durations_ms[tf] / analytics.test_counts[tf]) / 1000).toFixed(2) + "s"
          : "0s"
      }));
    }
  };

  const PassRateLineChart = () => {
    if (!analytics?.latest_10_groups_pass_percentages) return null;
    
    const data = analytics.latest_10_groups_pass_percentages
      .filter(group => group.total_tests > 0)
      .slice(0, 10)
      .reverse();
    
    if (data.length === 0) return null;
    
    const chartHeight = 120;
    const latestPassRate = data[data.length - 1]?.pass_percentage || 0;
    
    const chartOptions = {
      chart: {
        type: 'line',
        height: chartHeight,
        toolbar: {
          show: false,
        },
        background: 'transparent',
        fontFamily: 'inherit',
        sparkline: {
          enabled: false,
        },
        events: {
          markerClick: function(event, chartContext, { seriesIndex, dataPointIndex, w }) {
            const groupData = data[dataPointIndex];
            if (groupData?.group_id) {
              navigate(`/test/group/${groupData.group_id}`);
            }
          }
        },
        selection: {
          enabled: false
        },
      },
      theme: {
        mode: 'dark',
      },
      colors: ['#ffffff'],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      markers: {
        size: 3,
        hover: {
          size: 6,
        },
        strokeWidth: 0,
        fillOpacity: 1,
        colors: ['#ffffff'],
        cursor: 'pointer',
      },
      grid: {
        show: false,
      },
      xaxis: {
        categories: data.map((d, i) => `${i + 1}`),
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        min: 0,
        max: 100,
        labels: {
          show: false,
        },
      },
      tooltip: {
        theme: 'dark',
        style: {
          fontSize: '12px',
        },
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const groupData = data[dataPointIndex];
          return `
            <div style="padding: 8px 12px; background: #2a2a2a; border: 1px solid #333; border-radius: 6px;">
              <div style="color: #fff; font-weight: 500;">${groupData.pass_percentage}%</div>
              <div style="color: #888; font-size: 11px;">${formatDate(groupData.created_at)}</div>
              <div style="color: #888; font-size: 11px;">${groupData.passed_tests}/${groupData.total_tests} tests</div>
              <div style="color: #4a90e2; font-size: 10px; margin-top: 4px;">Click to view details</div>
            </div>
          `;
        },
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'vertical',
          shadeIntensity: 0.2,
          gradientToColors: ['rgba(255, 255, 255, 0.05)'],
          inverseColors: false,
          opacityFrom: 0.3,
          opacityTo: 0.05,
          stops: [0, 100],
        },
      },
      legend: {
        show: false,
      },
    };

    const series = [{
      name: 'Pass Rate',
      data: data.map(d => d.pass_percentage),
    }];
    
    return (
      <Card sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", p: { xs: 1.5, sm: 2 } }}>
        <Typography sx={{ 
          color: "#fff", 
          fontSize: { xs: "0.75rem", sm: "0.875rem" }, 
          fontWeight: 500, 
          mb: { xs: 1.5, sm: 2 }
        }}>
          Pass Rate Trend (Last 10 Runs)
        </Typography>
        <Box sx={{ position: "relative", width: "100%", height: chartHeight }}>
          <Chart
            options={chartOptions}
            series={series}
            type="area"
            height={chartHeight}
            width="100%"
          />
          <Box
            sx={{
              position: "absolute",
              top: { xs: 4, sm: 8 },
              left: { xs: 4, sm: 8 },
              backgroundColor: "rgba(26, 26, 26, 0.8)",
              borderRadius: 1,
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              border: "1px solid #333",
            }}
          >
            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              {latestPassRate}%
            </Typography>
            <Typography
              sx={{
                color: "#888",
                fontSize: { xs: "0.625rem", sm: "0.75rem" },
                textAlign: "center",
              }}
            >
              Latest
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  const StatCardWithTimeframe = ({ title, getData, icon, color, format = "number" }) => {
    const [timeframe, setTimeframe] = useState("last_30d");
    
    const value = getData(timeframe);
    
    return (
      <Card sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a", height: "100%" }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: { xs: 1.5, sm: 2 } }}>
            <Box
              sx={{
                backgroundColor: `${color}20`,
                borderRadius: 1,
                p: { xs: 0.75, sm: 1 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {icon}
            </Box>
            <Select
              value={timeframe}
              onChange={(e) => setTimeframe(e.target.value)}
              size="small"
              sx={{
                backgroundColor: "#252525",
                color: "#888",
                fontSize: { xs: "0.625rem", sm: "0.75rem" },
                minWidth: { xs: 60, sm: 80 },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#333",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#444",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#4a90e2",
                },
                "& .MuiSelect-select": {
                  py: { xs: 0.25, sm: 0.5 },
                  px: { xs: 0.5, sm: 1 },
                },
                "& .MuiSvgIcon-root": {
                  color: "#888",
                  fontSize: { xs: "1rem", sm: "1.25rem" },
                },
              }}
            >
              <MenuItem value="last_24h">24h</MenuItem>
              <MenuItem value="last_7d">7d</MenuItem>
              <MenuItem value="last_30d">30d</MenuItem>
            </Select>
          </Box>
          <Typography sx={{ color: "#888", fontSize: { xs: "0.75rem", sm: "0.875rem" }, mb: 1 }}>
            {title}
          </Typography>
                  <Typography
          sx={{
            color: "#fff",
            fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
            {format === "duration" ? value : value.toLocaleString()}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  return (
    <>
      <NavBar user={user} />
      <Box
        sx={{
          minHeight: "100vh",
          pt: 8,
          px: { xs: 2, sm: 3, md: 4 },
          ml: { xs: "64px", sm: "64px", md: "64px" }, // Account for sidebar width
          backgroundColor: "#121212",
        }}
      >
        <Box
          sx={{
            maxWidth: "1400px",
            margin: "0 auto",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              gap: { xs: 3, md: 0 },
              mb: 4,
            }}
          >
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: "#fff",
                  mb: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
                }}
              >
                <span style={{ fontSize: '1.2em' }}>{getGreeting().emoji}</span>
                {getGreeting().text}, {user?.full_name?.split(" ")[0]}
              </Typography>
              <Typography sx={{ 
                color: "#888", 
                fontSize: { xs: "0.75rem", sm: "0.875rem" }
              }}>
                Monitor your test suite performance and trends
              </Typography>
            </Box>
            <FormControl sx={{ 
              minWidth: { xs: "100%", sm: 200 },
              maxWidth: { xs: "100%", sm: "none" }
            }}>
              <InputLabel
                id="studio-select-label"
                sx={{
                  color: "#888",
                  "&.Mui-focused": { color: "#fff" },
                }}
              >
                Select Studio
              </InputLabel>
              <Select
                labelId="studio-select-label"
                value={selectedStudio}
                onChange={(e) => {
                  const newStudio = e.target.value;
                  console.log('Overview - onChange triggered with:', newStudio);
                  setSelectedStudioWithLogging(newStudio);
                  localStorage.setItem('selectedStudio', newStudio);
                  console.log('Overview - Saved to localStorage:', localStorage.getItem('selectedStudio'));
                }}
                label="Select Studio"
                sx={{
                  backgroundColor: "#1a1a1a",
                  border: "1px solid #2a2a2a",
                  color: "#fff",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2a2a2a",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#333",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#4a90e2",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                }}
              >
                {studios.map((studio) => (
                  <MenuItem key={studio.id} value={studio.id}>
                    {studio.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "400px",
              }}
            >
              <CircularProgress sx={{ color: "#fff" }} />
            </Box>
          ) : (
            <>
              {/* Analytics Section */}
              {isLoadingAnalytics ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                  }}
                >
                  <CircularProgress size={32} sx={{ color: "#fff" }} />
                </Box>
              ) : analytics ? (
                <Box sx={{ mb: 6 }}>
                  <Typography sx={{ 
                    color: "#fff", 
                    fontSize: { xs: "1.125rem", sm: "1.25rem" },
                    fontWeight: 500, 
                    mb: 3 
                  }}>
                    Analytics
                  </Typography>

                  {/* Stat Cards Grid */}
                  <Box sx={{ 
                    display: "grid", 
                    gridTemplateColumns: { 
                      xs: "repeat(auto-fit, minmax(160px, 1fr))",
                      sm: "repeat(auto-fit, minmax(200px, 1fr))",
                      md: "repeat(auto-fit, minmax(240px, 1fr))",
                      lg: "repeat(auto-fit, minmax(280px, 1fr))"
                    }, 
                    gap: { xs: 1.5, sm: 2, md: 2.5, lg: 3 },
                    mb: 4 
                  }}>
                    {/* Total Tests */}
                    <StatCardWithTimeframe
                      title="Total Tests Run"
                      getData={(tf) => analytics.test_counts?.[tf] || 0}
                      icon={<CheckCircle sx={{ color: "#4CAF50" }} />}
                      color="#4CAF50"
                    />

                    {/* Failed Tests */}
                    <StatCardWithTimeframe
                      title="Total Failed Tests"
                      getData={(tf) => analytics.failed_test_counts?.[tf] || 0}
                      icon={<BugReport sx={{ color: "#F44336" }} />}
                      color="#F44336"
                    />

                    {/* Total Duration */}
                    <StatCardWithTimeframe
                      title="Total Duration"
                      getData={(tf) => formatDuration(analytics.total_durations_ms?.[tf] || 0)}
                      icon={<Schedule sx={{ color: "#2196F3" }} />}
                      color="#2196F3"
                      format="duration"
                    />

                    {/* Total Test Cases */}
                    <Card sx={{ backgroundColor: "#1a1a1a", border: "1px solid #2a2a2a" }}>
                      <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                          <Box
                            sx={{
                              backgroundColor: "#FF980020",
                              borderRadius: 1,
                              p: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <PlayArrow sx={{ color: "#FF9800" }} />
                          </Box>
                        </Box>
                        <Typography sx={{ color: "#888", fontSize: "0.875rem", mb: 1 }}>
                          Total Test Cases
                        </Typography>
                        <Typography
                          sx={{
                            color: "#fff",
                            fontSize: "2.5rem",
                            fontWeight: 600,
                            lineHeight: 1,
                          }}
                        >
                          {analytics.total_test_cases || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>

                  {/* Pass Rate Line Chart */}
                  <PassRateLineChart />
                </Box>
              ) : null}

              {/* Test Groups Section */}
              <Box>
                <Typography
                  sx={{
                    color: "#fff",
                    fontSize: { xs: "1.125rem", sm: "1.25rem" },
                    fontWeight: 500,
                    mb: 3,
                  }}
                >
                  Test Run Groups
                </Typography>

                {isLoadingGroups ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "200px",
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: "#fff" }} />
                  </Box>
                ) : filterOldRunningTests(groups).length > 0 ? (
                  <Box sx={{ overflowX: "auto" }}>
                    <TableContainer
                      component={Paper}
                      sx={{
                        backgroundColor: "#1a1a1a",
                        border: "1px solid #2a2a2a",
                        minWidth: { xs: 600, sm: "100%" },
                      }}
                    >
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ 
                              color: "#888", 
                              borderBottom: "1px solid #2a2a2a", 
                              minWidth: { xs: 60, sm: 80 },
                              maxWidth: { xs: 80, sm: 120 }, 
                              width: { xs: 70, sm: 100 },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }}>
                              Group ID
                            </TableCell>
                            <TableCell sx={{ 
                              color: "#888", 
                              borderBottom: "1px solid #2a2a2a",
                              minWidth: { xs: 120, sm: 150 },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }}>
                              Created At
                            </TableCell>
                            <TableCell sx={{ 
                              color: "#888", 
                              borderBottom: "1px solid #2a2a2a",
                              minWidth: { xs: 80, sm: 100 },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }}>
                              Total Tests
                            </TableCell>
                            <TableCell sx={{ 
                              color: "#888", 
                              borderBottom: "1px solid #2a2a2a",
                              minWidth: { xs: 100, sm: 120 },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }}>
                              Pass Rate
                            </TableCell>
                            <TableCell sx={{ 
                              color: "#888", 
                              borderBottom: "1px solid #2a2a2a",
                              minWidth: { xs: 80, sm: 100 },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }}>
                              Duration
                            </TableCell>
                            <TableCell sx={{ 
                              color: "#888", 
                              borderBottom: "1px solid #2a2a2a",
                              minWidth: { xs: 120, sm: 150 },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }}>
                              Status
                            </TableCell>
                            <TableCell sx={{ 
                              color: "#888", 
                              borderBottom: "1px solid #2a2a2a",
                              minWidth: { xs: 80, sm: 100 },
                              fontSize: { xs: "0.75rem", sm: "0.875rem" },
                              textAlign: "center"
                            }}>
                              Insights
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filterOldRunningTests(groups).map((group) => (
                            <TableRow
                              key={group.group_id}
                              hover
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#252525",
                                },
                              }}
                            >
                                                            <TableCell sx={{ 
                                color: "#666", 
                                borderBottom: "1px solid #2a2a2a", 
                                minWidth: { xs: 60, sm: 80 }, 
                                maxWidth: { xs: 80, sm: 120 }, 
                                width: { xs: 70, sm: 100 }
                              }}>
                                {group.total_playwright_tests === 0 ? (
                                  <Tooltip
                                    title={
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <AccessTime sx={{ fontSize: 16 }} />
                                        This test group is still in progress...
                                      </Box>
                                    }
                                    placement="top"
                                    arrow
                                  >
                                    <Typography
                                      sx={{
                                        fontFamily: "monospace",
                                        fontSize: { xs: "0.625rem", sm: "0.75rem" },
                                        opacity: 0.7,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: { xs: 60, sm: 100 },
                                        color: "#666",
                                        cursor: "help",
                                        "&:hover": {
                                          opacity: 1
                                        }
                                      }}
                                    >
                                      {group.group_id.substring(0, 6)}...
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  <Typography
                                    component="a"
                                    href={`/test/group/${group.group_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontSize: { xs: "0.625rem", sm: "0.75rem" },
                                      opacity: 0.7,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: { xs: 60, sm: 100 },
                                      color: "#666",
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                        opacity: 1
                                      }
                                    }}
                                  >
                                    {group.group_id.substring(0, 6)}...
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #2a2a2a" }}>
                                {group.total_playwright_tests === 0 ? (
                                  <Tooltip
                                    title={
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <AccessTime sx={{ fontSize: 16 }} />
                                        This test group is still in progress...
                                      </Box>
                                    }
                                    placement="top"
                                    arrow
                                  >
                                    <Typography
                                      sx={{
                                        color: "#fff",
                                        fontWeight: 500,
                                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                        cursor: "help",
                                        "&:hover": {
                                          color: "#ccc"
                                        }
                                      }}
                                    >
                                      {formatDate(group.created_at)}
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  <Typography
                                    component="a"
                                    href={`/test/group/${group.group_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: "#fff",
                                      fontWeight: 500,
                                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                      textDecoration: "none",
                                      "&:hover": {
                                        textDecoration: "underline",
                                        color: "#ccc"
                                      }
                                    }}
                                  >
                                    {formatDate(group.created_at)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ 
                                color: "#fff", 
                                borderBottom: "1px solid #2a2a2a",
                                fontSize: { xs: "0.75rem", sm: "0.875rem" }
                              }}>
                                {group.total_playwright_tests}
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #2a2a2a" }}>
                                {group.total_playwright_tests === 0 ? (
                                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <AccessTime sx={{ 
                                      color: "#888", 
                                      fontSize: { xs: 16, sm: 20 }
                                    }} />
                                  </Box>
                                ) : (
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box
                                      sx={{
                                        width: { xs: "60px", sm: "100px" },
                                        height: "8px",
                                        backgroundColor: "#2a2a2a",
                                        borderRadius: 1,
                                        overflow: "hidden",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: `${calculatePassPercentage(group.status_breakdown)}%`,
                                          height: "100%",
                                          backgroundColor:
                                            calculatePassPercentage(group.status_breakdown) >= 80
                                              ? "#4CAF50"
                                              : calculatePassPercentage(group.status_breakdown) >= 50
                                              ? "#FF9800"
                                              : "#F44336",
                                        }}
                                      />
                                    </Box>
                                    <Typography sx={{ 
                                      color: "#888", 
                                      fontSize: { xs: "0.75rem", sm: "0.875rem" }
                                    }}>
                                      {calculatePassPercentage(group.status_breakdown)}%
                                    </Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ 
                                color: "#888", 
                                borderBottom: "1px solid #2a2a2a",
                                fontSize: { xs: "0.75rem", sm: "0.875rem" }
                              }}>
                                {group.total_duration_formatted}
                              </TableCell>
                              <TableCell sx={{ borderBottom: "1px solid #2a2a2a" }}>
                                {group.total_playwright_tests === 0 ? (
                                  <Typography sx={{ 
                                    color: "#FFA726", 
                                    fontSize: { xs: "0.625rem", sm: "0.875rem" },
                                    fontWeight: 500
                                  }}>
                                    Running...
                                  </Typography>
                                ) : (
                                  <Typography sx={{ 
                                    color: "#888", 
                                    fontSize: { xs: "0.625rem", sm: "0.875rem" }
                                  }}>
                                    {[
                                      group.status_breakdown.passed > 0 && `${group.status_breakdown.passed} passed`,
                                      group.status_breakdown.failed > 0 && `${group.status_breakdown.failed} failed`,
                                      group.status_breakdown.skipped > 0 && `${group.status_breakdown.skipped} skipped`
                                    ].filter(Boolean).join(', ') || 'No tests'}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell sx={{ 
                                borderBottom: "1px solid #2a2a2a",
                                textAlign: "center"
                              }}>
                                {group.insights && Array.isArray(group.insights) && group.insights.length > 0 ? (
                                  <Tooltip
                                    title={formatInsights(group.insights, group.group_id)}
                                    placement="left"
                                    arrow
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          maxWidth: '400px',
                                          fontSize: '0.875rem',
                                          padding: '12px 16px',
                                          backgroundColor: '#2a2a2a',
                                          border: '1px solid #333',
                                          borderRadius: '8px',
                                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                                        },
                                      },
                                    }}
                                  >
                                    <IconButton
                                      size="small"
                                      sx={{
                                        color: "#FFA726",
                                        "&:hover": {
                                          backgroundColor: "rgba(255, 167, 38, 0.1)",
                                        },
                                      }}
                                    >
                                      <Lightbulb sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  <Typography sx={{ color: "#666", fontSize: "0.75rem" }}>
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      p: { xs: 4, sm: 6 },
                      textAlign: "center",
                      border: "1px dashed #333",
                      borderRadius: 1,
                      backgroundColor: "#1a1a1a",
                    }}
                  >
                    <Typography sx={{ 
                      color: "#888", 
                      fontSize: { xs: "0.75rem", sm: "0.875rem" }
                    }}>
                      No test groups found for this studio.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Overview; 